import React, { useState } from "react";

import { useToolbarButtons } from "contexts/toolbars/hooks";

import { ButtonRenderer } from "./ButtonRenderer";
import { TOOLBAR_COLORS, TOOLBAR_CONTAINER } from "./constants";
import { useToolbarLayer } from "./ToolbarLayerContext";
import { ToolbarLayerProvider } from "./ToolbarLayerProvider";

type Alignment = "horizontal" | "vertical";
type Variant = "default" | "fly-out";
type FlyoutPlacement = "left" | "right";

interface ToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
    alignment?: Alignment;
    variant?: Variant;
    flyoutPlacement?: FlyoutPlacement;
}

/**
 * Managed toolbar component using centralized state management.
 * Buttons must be registered via dispatch actions before rendering.
 *
 * - Default variant: tooltip visibility is driven by the nearest ToolbarLayerProvider (e.g. ToolbarColumn).
 * - Fly-out variant: creates its own nested ToolbarLayerProvider so its tooltips respond only to hover
 *   on the fly-out itself, independent of any parent column hover state.
 */
export const Toolbar = ({
    alignment = "vertical",
    variant = "default",
    flyoutPlacement = "left",

    id,
    style,
    ...props
}: ToolbarProps) => {
    if (!id) {
        throw new Error("Toolbar component requires an 'id' prop for state management");
    }

    const [isHovered, setIsHovered] = useState(false);
    const { tooltipsVisible: parentTooltipsVisible } = useToolbarLayer();
    const visibleButtons = useToolbarButtons(id);

    if (visibleButtons.length === 0) {
        return null;
    }

    const baseStyle: React.CSSProperties = {
        display: "flex",
        flexDirection: alignment === "vertical" ? "column" : "row",
        pointerEvents: "auto",
        width: `${TOOLBAR_CONTAINER.WIDTH}px`,
        alignItems: "center",
    };

    const variantStyles: Record<Variant, React.CSSProperties> = {
        default: {
            padding: `${TOOLBAR_CONTAINER.PADDING}px`,
            gap: `${TOOLBAR_CONTAINER.GAP}px`,
            backgroundColor: TOOLBAR_CONTAINER.BG,
            borderRadius: `${TOOLBAR_CONTAINER.RADIUS}px`,
            border: TOOLBAR_CONTAINER.BORDER,
            boxShadow: TOOLBAR_CONTAINER.SHADOW,
        },
        "fly-out": {
            padding: `${TOOLBAR_CONTAINER.PADDING}px`,
            gap: `${TOOLBAR_CONTAINER.GAP}px`,
            boxShadow: TOOLBAR_CONTAINER.SHADOW,
        },
    };

    let dynamicFlyoutStyle: React.CSSProperties = {};
    if (variant === "fly-out") {
        if (flyoutPlacement === "left") {
            dynamicFlyoutStyle = {
                borderRadius: `${TOOLBAR_CONTAINER.RADIUS}px 0px ${TOOLBAR_CONTAINER.RADIUS}px ${TOOLBAR_CONTAINER.RADIUS}px`,
                background: `linear-gradient(90deg, ${TOOLBAR_COLORS.WHITE} 67.05%, ${TOOLBAR_COLORS.FLYOUT_GREY} 127.27%)`,
                marginRight: `${TOOLBAR_CONTAINER.RADIUS}px`,
            };
        } else {
            dynamicFlyoutStyle = {
                borderRadius: `0px ${TOOLBAR_CONTAINER.RADIUS}px ${TOOLBAR_CONTAINER.RADIUS}px ${TOOLBAR_CONTAINER.RADIUS}px`,
                background: `linear-gradient(-90deg, ${TOOLBAR_COLORS.WHITE} 67.05%, ${TOOLBAR_COLORS.FLYOUT_GREY} 127.27%)`,
                marginLeft: `${TOOLBAR_CONTAINER.RADIUS}px`,
            };
        }
    }

    const toolbarStyle: React.CSSProperties = {
        ...baseStyle,
        ...variantStyles[variant],
        ...style,
        ...dynamicFlyoutStyle,
    };

    const toolbarDiv = (
        <div
            role="toolbar"
            style={toolbarStyle}
            id={id}
            onMouseEnter={variant === "fly-out" ? () => setIsHovered(true) : undefined}
            onMouseLeave={variant === "fly-out" ? () => setIsHovered(false) : undefined}
            {...props}
        >
            <ButtonRenderer toolbarId={id} />
        </div>
    );

    if (variant === "fly-out") {
        return (
            <ToolbarLayerProvider tooltipsVisibleOverride={isHovered && parentTooltipsVisible}>
                {toolbarDiv}
            </ToolbarLayerProvider>
        );
    }

    return toolbarDiv;
};
